<script lang="ts">
  import {CatalystApp} from 'Catalyst/components/CatalystApp';
  import type {CssColor} from 'Catalyst/utility/theme';
  import {centsToString} from 'Utility/formatters/currency';
  import {integerMask} from 'Utility/formatters/mask';
  import {numberOfSecurities} from 'Utility/investorFlow/v2/amountSelection';
  import {incentivePlans} from 'Store/deals/incentivePlan';
  import {whiteLabeling} from 'Store/WhiteLabelStore';

  export let amount: number; // In cents
  export let pricePerSecurity: number; // In cents
  export let exchangePrice: boolean;
  export let hideUnitConverter: boolean;
  export let currencySymbol: string;
  export let numSecurities: number;

  $: finalPrice =
    $incentivePlans?.final_price?.amount_cents || pricePerSecurity;
  $: fullPrice = $incentivePlans?.full_price?.amount_cents;
  $: originalNumberOfSecurities = numberOfSecurities(amount, fullPrice);
  $: originalInvestmentAmount =
    numberOfSecurities(amount, finalPrice) * fullPrice;
  $: amountMatches = numSecurities === originalNumberOfSecurities;

  $: brandColor = $whiteLabeling.primary_color as CssColor;
</script>

<CatalystApp {brandColor} class="block w-full">
  <div
    data-testid="preset-amount-{amount}"
    class="amount pointer w-full"
    class:selected={amountMatches}
    on:click
    on:keydown
    role="button"
    tabindex="0"
  >
    <div
      class="flex justify-center items-center w-full"
      class:reverse={!exchangePrice}
    >
      <div
        class="text-center"
        class:light-gray={!exchangePrice}
        class:selected-text={amountMatches}
      >
        {#if !exchangePrice && $incentivePlans?.effective_tier}
          <s data-testid="original-investment-amount-{amount}"
            >{currencySymbol}{centsToString(originalInvestmentAmount, {
              removeZeroCents: true,
            })}</s
          >
        {/if}
        <span data-testid="investment-amount-{amount}"
          >{currencySymbol}{centsToString(amount, {
            removeZeroCents: true,
          })}</span
        >
      </div>
      {#if !hideUnitConverter}
        <div class="light-gray" class:selected-text={amountMatches}>&#9679</div>
        <div
          class:light-gray={exchangePrice}
          class:selected-text={amountMatches}
        >
          {#if exchangePrice && $incentivePlans?.effective_tier}
            <s data-testid="original-number-of-securities-{amount}"
              >{integerMask(originalNumberOfSecurities)}</s
            >
          {/if}
          <span data-testid="number-of-securities-{amount}"
            >{integerMask(numberOfSecurities(amount, finalPrice))}</span
          >
        </div>
      {/if}
    </div>
  </div>
</CatalystApp>

<style lang="postcss">
  .amount {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0.25rem 0.75rem;
    width: 95%;
    height: 2.25rem;
    background: var(
      --dm-bg-secondary-color,
      var(--palettes-neutral-75, #f2f4f7)
    );
    border-radius: 62.5rem;
    border: 1px solid transparent;
  }

  .pointer {
    cursor: pointer;
  }

  .light-gray {
    color: var(--dm-text-secondary-color, var(--palettes-neutral-575, #667085));
    margin-left: 0.5rem;
  }

  .reverse {
    flex-direction: row-reverse;
  }

  .selected {
    font-weight: 400;
    border-color: var(--palettes-brand-color-600);
    background: var(--palettes-brand-color-50);
  }

  .selected-text {
    color: var(--palettes-brand-color-700);
  }
</style>
